import { useEffect } from 'react';

import useFeatures from '../../../hooks/useFeatures';
import useBillingPlan from '../../../hooks/useBillingPlan';
import { useDashboardStore } from '../../../../store/models';
import useWhiteLabel from '../../../../store/models/styles/useWhiteLabel';
import {
    isEnterprise,
    isFreeTrial,
    isFreePlan,
} from '../../../../utils';
import { getCurrencySymbol } from '../../../../conf/chargify';
import { BillingCoupons } from '../../../../utils/constants';

function isWhiteLabel(whiteLabelSettings) {
    return whiteLabelSettings.labelName !== 'ecal';
}

function isProviderUpgradable(provider) {
    return provider === 'ecal' || provider === 'chargify';
}

function isCustomProvider(provider) {
    return provider === 'custom';
}

function useEcalPlanState(props) {
    const { publisher } = props;

    const { features } = useFeatures();
    const dashboardStore = useDashboardStore();

    const {
        publisherPlan,
        isUpgrading,
        coupon,
    } = useBillingPlan();

    const { whiteLabelSettings } = useWhiteLabel();

    useEffect(() => {
        if (coupon === BillingCoupons.EXPERTPLS) {
            window.Intercom('showMessages');
        }
    }, [coupon]);

    // Derived card display values
    const planName = publisherPlan?.name || publisher.planName || 'Community';
    const hasCurrentPlan = !!publisherPlan?.name;
    const currencySymbol = getCurrencySymbol(publisherPlan?.currency);
    // Custom-provider plans are priced off-platform, so the price tag is hidden
    const planPrice = isCustomProvider(publisherPlan?.provider) ? null : publisherPlan?.price;
    const isUnlimitedSubscribers = publisher.subscriberLimit === -1;
    const total = isUnlimitedSubscribers ? 0 : (Number(publisher.subscriberLimit) || 0);
    const remaining = total - (Number(dashboardStore.totalSubscribers) || 0);
    const used = total - remaining;
    const usedPercent = total > 0 ? Math.min((used / total) * 100, 100) : 0;
    const showUsageBar = !isEnterprise(publisherPlan) && !isUnlimitedSubscribers && total > 0;

    const isFreeOrTrialPlan = isFreeTrial(publisherPlan?.name) || isFreePlan(publisherPlan?.name);

    const showBilling = isProviderUpgradable(publisherPlan?.provider)
        && !isEnterprise(publisherPlan)
        && !isWhiteLabel(whiteLabelSettings)
        && features.billingActive;

    const showAwsBillingInfo = publisherPlan?.provider === 'aws';

    const showBillingAlt = publisherPlan?.provider === 'aws'
        ? false
        : (!isProviderUpgradable(publisherPlan?.provider) || !features.billingActive);

    const showAddOns = showBilling && !isFreeOrTrialPlan;

    function handleUpgradeClick() {
        if (whiteLabelSettings.labelName === 'ecal') {
            window.Intercom('showMessages');
        } else {
            window.open(whiteLabelSettings.supportContact, '_blank');
        }
    }

    return {
        publisherPlan,
        isUpgrading,
        planName,
        hasCurrentPlan,
        currencySymbol,
        planPrice,
        total,
        remaining,
        used,
        usedPercent,
        showUsageBar,
        isUnlimitedSubscribers,
        showBilling,
        showAddOns,
        showBillingAlt,
        showAwsBillingInfo,
        isFreeOrTrialPlan,
        supportLabel: whiteLabelSettings.supportLabel,
        handleUpgradeClick,
    };
}

export default useEcalPlanState;
